import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MapPin, Compass, Layers, Users, Loader2, Home, Menu, Calendar, LogIn, Shield, LogOut, Navigation } from 'lucide-react';
import ExhibitionScene3D from '@/components/ExhibitionScene3D';
import BoothInfoPanel from '@/components/BoothInfoPanel';
import SearchBar from '@/components/SearchBar';
import FilterPanel from '@/components/FilterPanel';
import WeatherWidget from '@/components/WeatherWidget';
import VisitorPanel from '@/components/VisitorPanel';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import LanguageToggle from '@/components/LanguageToggle';
import WelcomePanel from '@/components/WelcomePanel';
import { authApi } from '@/lib/api';
import { useVisitorGPS } from '@/hooks/useVisitorGPS';
import GPSLocateButton from '@/components/GPSLocateButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExhibitionPage() {
  const { t } = useTranslation();
  const [booths, setBooths] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [sceneProps, setSceneProps] = useState([]);
  const [landmarks, setLandmarks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [halls, setHalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedHall, setSelectedHall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);
  const [weather, setWeather] = useState(null);
  const gps = useVisitorGPS();
  const [effectsEnabled, setEffectsEnabled] = useState(false); // OFF by default for clear view
  const [showFacilities, setShowFacilities] = useState(true);
  const [routeEnd, setRouteEnd] = useState(null); // [x, 0, z]
  const [routeEndBooth, setRouteEndBooth] = useState(null);
  const routeStart = [-75, 0, 25]; // Entrance archway position (left side)

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [boothsRes, facilitiesRes, categoriesRes, hallsRes, statsRes, propsRes, landmarksRes] = await Promise.all([
          axios.get(`${API_URL}/booths`),
          axios.get(`${API_URL}/facilities`),
          axios.get(`${API_URL}/categories`),
          axios.get(`${API_URL}/halls`),
          axios.get(`${API_URL}/stats`),
          axios.get(`${API_URL}/props`),
          axios.get(`${API_URL}/landmarks`),
        ]);

        setBooths(boothsRes.data.data || []);
        setFacilities(facilitiesRes.data.data || []);
        setCategories(categoriesRes.data.data || []);
        setHalls(hallsRes.data.data || []);
        setStats(statsRes.data.data);
        setSceneProps(propsRes.data.data || []);
        setLandmarks(landmarksRes.data.data || []);
      } catch (error) {
        console.error('Error fetching exhibition data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleNavigateToBooth = (booth) => {
    setRouteEnd([booth.position_x, 0, booth.position_z]);
    setRouteEndBooth(booth);
    setSelectedBooth(null);
  };

  const handleBoothSelect = async (booth) => {
    if (!booth) { setSelectedBooth(null); return; }
    setSelectedBooth(booth);
    try {
      const res = await axios.get(`${API_URL}/booths/${booth.id}`);
      const full = res.data?.data;
      if (full) {
        setSelectedBooth((current) => (current && current.id === booth.id ? { ...current, ...full } : current));
      }
    } catch (err) {
      console.error('Failed to load booth details', err);
    }
  };

  const clearRoute = () => {
    setRouteEnd(null);
    setRouteEndBooth(null);
  };

  const filteredBooths = useMemo(() => {
    let filtered = [...booths];

    if (selectedCategory) {
      filtered = filtered.filter(b => b.category_id === selectedCategory);
    }

    if (selectedHall) {
      filtered = filtered.filter(b => b.hall_id === selectedHall);
    }

    return filtered;
  }, [booths, selectedCategory, selectedHall]);

  const positionedBooths = filteredBooths;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
          <div className="text-2xl font-bold text-white mb-2">{t('welcome.greeting')}</div>
          <div className="text-slate-400">{t('app.subtitle')} · {t('app.location')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[760px] sm:min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 overflow-y-auto overflow-x-hidden">
      {/* Top Navigation Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-900/90 via-emerald-800/90 to-emerald-900/90 backdrop-blur-xl rounded-2xl px-4 py-2.5 border border-emerald-500/30 shadow-xl" data-testid="app-logo">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center">
              <img src="/hln-logo.png" alt="HLN 2026 Logo" className="w-full h-full object-contain drop-shadow-lg" />
            </div>
            <div className="hidden sm:block">
              <div className="text-white font-black text-xs leading-none tracking-wider">{t('app.title')}</div>
              <div className="text-emerald-300 text-[10px] font-bold">{t('app.subtitle')}</div>
              <div className="text-emerald-400/70 text-[9px] mt-0.5">📍 {t('app.location')}</div>
            </div>
          </div>

          <div className="flex-1 max-w-2xl">
            <SearchBar onBoothSelect={handleBoothSelect} />
          </div>

          <Link to="/schedule" className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white hover:bg-slate-800/70 transition-colors shadow-xl text-xs font-bold" data-testid="nav-schedule-btn">
            <Calendar className="w-4 h-4 text-emerald-400" /><span className="hidden lg:inline">{t('nav.schedule')}</span>
          </Link>

          {authApi.isAuthenticated() ? (
            <>
              {authApi.isAdmin() && (
                <Link to="/admin" className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-emerald-500/20 backdrop-blur-xl border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/30 transition-colors shadow-xl text-xs font-bold" data-testid="nav-admin-btn">
                  <Shield className="w-4 h-4" /><span className="hidden lg:inline">{t('nav.admin')}</span>
                </Link>
              )}
              <button onClick={() => { authApi.logout(); window.location.reload(); }} className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-red-500/20 backdrop-blur-xl border border-red-500/30 text-red-200 hover:bg-red-500/30 transition-colors shadow-xl text-xs font-bold" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/login" className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white hover:bg-slate-800/70 transition-colors shadow-xl text-xs font-bold" data-testid="nav-login-btn">
              <LogIn className="w-4 h-4 text-emerald-400" /><span className="hidden lg:inline">{t('nav.login')}</span>
            </Link>
          )}

          <LanguageToggle />

          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-3 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white hover:bg-slate-800/70 transition-colors shadow-xl"
            data-testid="toggle-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <button
            onClick={() => setShowFacilities(!showFacilities)}
            className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-wider shadow-lg transition-all flex items-center gap-1.5 ${
              showFacilities
                ? 'bg-blue-500 text-white'
                : 'bg-slate-800/70 text-slate-300 border border-slate-700'
            }`}
            data-testid="toggle-facilities-btn"
          >
            <span>🏛️</span>
            {showFacilities ? 'HIDE FACILITIES' : 'SHOW FACILITIES'} ({facilities.length})
          </button>

          <div className="hidden md:flex items-center gap-1.5">
            {[
              {name: 'HIJAU', color: 'bg-green-500', text: 'text-green-100'},
              {name: 'INOVASI', color: 'bg-blue-500', text: 'text-blue-100'},
              {name: 'KOMUNITI', color: 'bg-pink-500', text: 'text-pink-100'},
              {name: 'LESTARI', color: 'bg-orange-500', text: 'text-orange-100'},
            ].map((theme) => (
              <div key={theme.name} className={`px-3 py-1 rounded-full ${theme.color} ${theme.text} text-[10px] font-black tracking-wider shadow-lg`}>
                {theme.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      <WeatherWidget
        onWeatherChange={setWeather}
        effectsEnabled={effectsEnabled}
        onToggleEffects={() => setEffectsEnabled(v => !v)}
      />

      <VisitorPanel booths={booths} />
      <AnnouncementBanner />

      {sidebarOpen && (
        <div className="absolute left-4 top-56 bottom-24 w-72 z-20 animate-in slide-in-from-left duration-300 overflow-y-auto scrollbar-thin max-h-[calc(100svh-20rem)]">
          <FilterPanel
            categories={categories}
            halls={halls}
            selectedCategory={selectedCategory}
            selectedHall={selectedHall}
            onCategoryChange={setSelectedCategory}
            onHallChange={setSelectedHall}
            boothStats={stats}
          />
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
        <div className="bg-slate-900/70 backdrop-blur-xl rounded-2xl px-6 py-3 border border-slate-700/50 shadow-xl flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white text-sm font-medium">{t('stats.live')}</span>
          </div>
          <div className="w-px h-6 bg-slate-700" />
          <div className="flex items-center gap-2 text-white text-sm">
            <MapPin className="w-4 h-4 text-blue-400" />
            <span>{filteredBooths.length} {t('stats.booths')}</span>
          </div>
          <div className="w-px h-6 bg-slate-700" />
          <div className="flex items-center gap-2 text-white text-sm">
            <Layers className="w-4 h-4 text-purple-400" />
            <span>{halls.length} {t('stats.zones')}</span>
          </div>
          <div className="w-px h-6 bg-slate-700" />
          <div className="flex items-center gap-2 text-white text-sm">
            <Users className="w-4 h-4 text-emerald-400" />
            <span>{stats?.total_companies || 0} {t('stats.exhibitors')}</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 right-4 z-20">
        <div className="bg-slate-900/70 backdrop-blur-xl rounded-2xl p-3 border border-slate-700/50 shadow-xl" data-testid="compass">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
            <div className="absolute top-1 left-1/2 -translate-x-1/2 text-red-400 text-xs font-bold">N</div>
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-slate-400 text-xs font-bold">S</div>
            <div className="absolute left-1 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">W</div>
            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">E</div>
            <Compass className="absolute inset-0 m-auto w-6 h-6 text-blue-400" />
          </div>
        </div>
      </div>

      <div className="absolute inset-0 z-0" data-testid="exhibition-3d-scene">
        <ExhibitionScene3D
          booths={positionedBooths}
          facilities={showFacilities ? facilities : []}
          props={sceneProps}
          landmarks={landmarks}
          onBoothSelect={handleBoothSelect}
          selectedBooth={selectedBooth}
          weather={effectsEnabled ? weather : null}
          routeStart={routeEnd ? routeStart : null}
          routeEnd={routeEnd}
          routeActive={!!routeEnd}
          visitorWorldPos={gps.worldPos}
          visitorAccuracy={gps.accuracyMeters}
        />
      </div>

      <GPSLocateButton gps={gps} />

      {selectedBooth && (
        <BoothInfoPanel
          booth={selectedBooth}
          onClose={() => setSelectedBooth(null)}
          onNavigate={handleNavigateToBooth}
        />
      )}

      {routeEnd && routeEndBooth && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-30 animate-in slide-in-from-top duration-300" data-testid="route-active-banner">
          <div className="bg-gradient-to-r from-amber-500/90 to-orange-500/90 backdrop-blur-xl rounded-2xl px-4 py-3 border border-amber-400/50 shadow-2xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
              🎯
            </div>
            <div className="text-white">
              <div className="text-xs font-bold uppercase tracking-wider opacity-90">{t('route.navigating_to')}</div>
              <div className="font-black text-sm">{routeEndBooth.booth_number} - {routeEndBooth.company?.name || t('booth.no_company')}</div>
            </div>
            <button
              onClick={clearRoute}
              className="ml-2 p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              data-testid="clear-route-btn"
              aria-label="Clear route"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {!selectedBooth && showWelcome && (
        <WelcomePanel onClose={() => setShowWelcome(false)} />
      )}
    </div>
  );
}